<template>
  <div
    class="main-menu menu-fixed menu-accordion menu-shadow"
    :class="[
      { 'expanded': !isVerticalMenuCollapsed || (isVerticalMenuCollapsed && isMouseHovered) },
      skin === 'semi-dark' ? 'menu-light' : 'menu-dark'
    ]"
    @mouseenter="updateMouseHovered(true)"
    @mouseleave="updateMouseHovered(false)"
  >
    <!-- main menu header-->
    <div class="navbar-header expanded">
      <slot
        name="header"
        :toggle-vertical-menu-active="toggleVerticalMenuActive"
        :toggle-collapsed="toggleCollapsed"
        :collapse-toggler-icon="collapseTogglerIcon"
      >
        <ul class="nav navbar-nav flex-row">
          <!-- Logo & Text -->
          <li class="nav-item mr-auto">
            <b-link
              class="navbar-brand"
              to="/home/index"
            >
              <span class="brand-logo">
                <b-img
                  :src="appLogoImage"
                  alt="ADIN.AI"
                  class="light-logo"
                />
                <b-img
                  :src="appLogoDarkImage"
                  alt="ADIN.AI"
                  class="dark-logo"
                />
              </span>
              <!--<h2 class="brand-text">
                {{ appName }}
              </h2>-->
            </b-link>
          </li>

          <!-- Mobile Toggler Button - Only visible on mobile -->
          <li class="nav-item nav-toggle">
            <b-link class="nav-link modern-nav-toggle d-block d-xl-none">
              <feather-icon
                icon="XIcon"
                size="16"
                class="mobile-close"
                @click="toggleVerticalMenuActive"
              />
            </b-link>
          </li>
        </ul>
      </slot>
    </div>
    <!-- / main menu header-->
    <!-- Shadow -->
    <div
      :class="{'d-block': shallShadowBottom}"
      class="shadow-bottom"
    />
    <!-- main menu content-->
    <vue-perfect-scrollbar
      :settings="perfectScrollbarSettings"
      class="main-menu-content scroll-area"
      tagname="ul"
      :style="{ 'height': 'calc(100% - 145px)' }"
      @ps-scroll-y="evt => { shallShadowBottom = evt.srcElement.scrollTop > 0 }"
    >
      <vertical-nav-menu-items
        :items="navMenuItems"
        class="navigation navigation-main"
      />
    </vue-perfect-scrollbar>
    <!-- /main menu content-->
    
    <!-- menu footer -->
    <vertical-nav-menu-footer />
    <!-- /menu footer -->
    
    <!-- Menu Toggle Button - Only visible on desktop -->
    <div 
      v-if="currentBreakpoint === 'xl'"
      class="menu-toggle-button"
      @click="toggleCollapsed"
      :style="{ right: isVerticalMenuCollapsed ? '0' : '0', left: 'auto' }"
    >
      <feather-icon
        :icon="isVerticalMenuCollapsed ? 'ChevronRightIcon' : 'ChevronLeftIcon'"
        size="14"
      />
    </div>
  </div>
</template>

<script>
import navMenuItems from '@/navigation/vertical'
import VuePerfectScrollbar from 'vue-perfect-scrollbar'
import { BLink, BImg } from 'bootstrap-vue'
import { provide, computed, ref, onUnmounted } from '@vue/composition-api'
import useAppConfig from '@core/app-config/useAppConfig'
import { $themeConfig } from '@themeConfig'
import VerticalNavMenuItems from './components/vertical-nav-menu-items/VerticalNavMenuItems.vue'
import VerticalNavMenuFooter from './components/VerticalNavMenuFooter.vue'
import useVerticalNavMenu from './useVerticalNavMenu'

export default {
  components: {
    VuePerfectScrollbar,
    VerticalNavMenuItems,
    VerticalNavMenuFooter,
    BLink,
    BImg,
  },
  props: {
    isVerticalMenuActive: {
      type: Boolean,
      required: true,
    },
    toggleVerticalMenuActive: {
      type: Function,
      required: true,
    },
  },
  setup(props) {
    const {
      isMouseHovered,
      isVerticalMenuCollapsed,
      collapseTogglerIcon,
      toggleCollapsed,
      updateMouseHovered,
    } = useVerticalNavMenu(props)

    const { skin } = useAppConfig()

    // Shadow bottom is UI specific and can be removed by user => It's not in `useVerticalNavMenu`
    const shallShadowBottom = ref(false)

    // Get current breakpoint
    const currentBreakpoint = ref('xl')
    const updateBreakpoint = () => {
      if (window.innerWidth >= 1200) currentBreakpoint.value = 'xl'
      else if (window.innerWidth >= 992) currentBreakpoint.value = 'lg'
      else if (window.innerWidth >= 768) currentBreakpoint.value = 'md'
      else if (window.innerWidth >= 576) currentBreakpoint.value = 'sm'
      else currentBreakpoint.value = 'xs'
    }

    // Initial breakpoint check
    updateBreakpoint()

    // Add resize listener
    window.addEventListener('resize', updateBreakpoint)
    onUnmounted(() => {
      window.removeEventListener('resize', updateBreakpoint)
    })

    provide('isMouseHovered', isMouseHovered)

    const perfectScrollbarSettings = {
      maxScrollbarLength: 60,
      wheelPropagation: false,
      suppressScrollX: true,
    }

    const collapseTogglerIconFeather = computed(() => (collapseTogglerIcon.value === 'unpinned' ? 'ChevronRightIcon' : 'ChevronLeftIcon'))

    // App Name
    const { appName, appLogoImage, appLogoDarkImage } = $themeConfig.app
    return {
      navMenuItems,
      perfectScrollbarSettings,
      isVerticalMenuCollapsed,
      collapseTogglerIcon,
      toggleCollapsed,
      isMouseHovered,
      updateMouseHovered,
      collapseTogglerIconFeather,

      // Shadow Bottom
      shallShadowBottom,

      // Skin
      skin,

      // App Name
      appName,
      appLogoImage,
      appLogoDarkImage,

      currentBreakpoint,
    }
  },
}
</script>

<style lang="scss">
@import "~@core/scss/base/core/menu/menu-types/vertical-menu.scss";

.vertical-overlay-menu {
  .main-menu {
    position: fixed;
    top: 0;
    left: 0;
    height: 100vh;
    width: 260px;
    transform: translateX(-100%);
    transition: transform 0.25s ease-in-out;
    
    &.expanded {
      transform: translateX(0);
    }
  }
}

.main-menu {
  z-index: 1031;
  position: fixed;
  height: 100%;

  &.menu-shadow {
    box-shadow: 0 0 15px 0 rgba(0,0,0,.05);
  }

  .navigation {
    li {
      position: relative;
      
      &.has-sub {
        > a {
          position: relative;
        }
      }
    }
  }
}

.scroll-area {
  height: calc(100% - 74px) !important;
  position: relative;
}

.main-menu.menu-fixed.menu-accordion.menu-shadow:not(.expanded) {
  .navigation {
    li {
      a {
        .menu-title {
          display: none;
        }
      }
      
      &.has-sub.open, &.has-sub:hover {
        position: relative;
        
        .menu-content {
          z-index: 1050;
          right: auto !important;
          left: 74px !important; 
          width: auto !important;
          min-width: 200px;
          overflow: auto;
          position: absolute !important;
          background: var(--dark);
          border-radius: 4px;
          box-shadow: 0 0 10px rgba(0, 0, 0, 0.2);
          padding: 10px 0;
          max-height: none !important;
          height: auto !important;
          display: block !important;
          transform: none !important;
          top: 0;
          
          
          li {
            a {
              padding: 10px 15px;
              display: flex;
              align-items: center;
              color: var(--light);
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
              
              &:hover {
                background: rgba(255, 255, 255, 0.1);
              }
              
              .menu-title {
                display: block !important;
                margin-left: 10px;
              }
            }
          }
        }
      }
    }
  }
  

  &.menu-light:not(.expanded) {
    .navigation {
      li {
        &.has-sub.open, &.has-sub:hover {
          .menu-content {
            background: var(--light);
            box-shadow: 0 4px 24px 0 rgba(34, 41, 47, 0.1);
            
            li a {
              color: var(--dark);
              
              &:hover {
                background: rgba(0, 0, 0, 0.04);
              }
            }
          }
        }
      }
    }
  }
  

  .vertical-nav-menu-footer {
    .settings-wrapper {
      display: flex;
      justify-content: center;
      
      .settings-text {
        display: none !important;
      }
      
      .settings-btn {
        width: 40px !important;
        padding: 0 !important;
        justify-content: center !important;
      }
    }
    
    .user-section {
      .user-info-wrapper,
      .menu-dots,
      .logout-btn {
        display: none !important;
      }
      
      .user-avatar {
        margin: 0 auto;
      }
    }
  }
}

.menu-toggle-button {
  position: absolute;
  top: 20px;
  right: -10px;
  background: #6c757d;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 0 6px rgba(0, 0, 0, 0.15);
  transition: all 0.25s ease;
  z-index: 999;
  
  @media (max-width: 1199px) {
    display: none !important;
  }
  
  &:hover {
    transform: scale(1.08);
    box-shadow: 0 0 10px rgba(0, 0, 0, 0.2);
    background: #5a6268;
  }
  
  svg {
    color: white;
    stroke-width: 2.5px;
  }
}

.main-menu {
  overflow: hidden !important;
  z-index: 1000 !important;
  position: relative;
  height: 100%;
}

.main-menu-content.scroll-area {
  overflow-y: hidden !important;
  overflow-x: hidden !important;
  height: calc(100% - 145px) !important;
}

.navigation {
  overflow: visible !important;
}

html, body {
  overflow-x: hidden;
}

.main-menu-content.scroll-area {
  .ps__rail-y {
    z-index: 1060;
  }
  
  .ps {
    overflow: visible !important;
  }
  
  .ps__rail-x {
    display: none !important;
  }
}

.main-menu .navigation,
.main-menu .navigation li {
  overflow: visible !important;
}

.main-menu.menu-fixed.menu-accordion.menu-shadow {
  .navigation {
    overflow: visible !important;
  }
}

.menu-content {
  overflow: visible !important;
  height: auto !important;
}
</style>
